import { http } from '../../../../api/http';
import type {
  Trabajador,
  CreateTrabajadorRequest,
  TrabajadorConcepto,
  TrabajadorCentroCosto,
  RegimenLaboral,
  ModalidadContrato,
  SituacionTrabajador,
  CategoriaTrabajador,
  MotivoCese,
  Area,
  RegimenPensionario,
  ConceptoPlanilla,
} from '../types/trabajador';

export class TrabajadorService {
  private static readonly ENDPOINTS = {
    TRABAJADOR: '/planilla/trabajador',
    CATALOG: '/planilla/catalog',
    REGIMEN_PENSIONARIO: '/planilla/regimen-pensionario',
    CONCEPTO: '/planilla/concepto',
  };

  // ============ Trabajadores ============

  public static async getAll(): Promise<Trabajador[]> {
    try {
      const response = await http.get(this.ENDPOINTS.TRABAJADOR);
      return response.data;
    } catch (error) {
      console.error('Error fetching trabajadores:', error);
      throw error;
    }
  }

  public static async getById(id: number): Promise<Trabajador> {
    try {
      const response = await http.get(`${this.ENDPOINTS.TRABAJADOR}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching trabajador:', error);
      throw error;
    }
  }

  public static async search(term: string): Promise<Trabajador[]> {
    try {
      const response = await http.get(`${this.ENDPOINTS.TRABAJADOR}/search`, { params: { term } });
      return response.data;
    } catch (error) {
      console.error('Error searching trabajadores:', error);
      throw error;
    }
  }

  public static async save(trabajador: Trabajador): Promise<Trabajador> {
    try {
      const response = await http.post(this.ENDPOINTS.TRABAJADOR, trabajador);
      return response.data;
    } catch (error) {
      console.error('Error saving trabajador:', error);
      throw error;
    }
  }

  public static async createWithPersonal(request: CreateTrabajadorRequest): Promise<Trabajador> {
    try {
      const response = await http.post(`${this.ENDPOINTS.TRABAJADOR}/with-personal`, request);
      return response.data;
    } catch (error) {
      console.error('Error saving trabajador with personal:', error);
      throw error;
    }
  }

  public static async delete(id: number): Promise<void> {
    try {
      await http.delete(`${this.ENDPOINTS.TRABAJADOR}/${id}`);
    } catch (error) {
      console.error('Error deleting trabajador:', error);
      throw error;
    }
  }

  // ============ Conceptos por Trabajador ============

  public static async getConceptos(idTrabajador: number): Promise<TrabajadorConcepto[]> {
    try {
      const response = await http.get(`${this.ENDPOINTS.TRABAJADOR}/${idTrabajador}/conceptos`);
      return response.data;
    } catch (error) {
      console.error('Error fetching conceptos:', error);
      throw error;
    }
  }

  public static async saveConcepto(idTrabajador: number, concepto: TrabajadorConcepto): Promise<TrabajadorConcepto> {
    try {
      const response = await http.post(`${this.ENDPOINTS.TRABAJADOR}/${idTrabajador}/conceptos`, concepto);
      return response.data;
    } catch (error) {
      console.error('Error saving concepto:', error);
      throw error;
    }
  }

  public static async deleteConcepto(idTrabajador: number, idTC: number): Promise<void> {
    try {
      await http.delete(`${this.ENDPOINTS.TRABAJADOR}/${idTrabajador}/conceptos/${idTC}`);
    } catch (error) {
      console.error('Error deleting concepto:', error);
      throw error;
    }
  }

  // ============ Centros de Costo ============

  public static async getCentrosCosto(idTrabajador: number): Promise<TrabajadorCentroCosto[]> {
    try {
      const response = await http.get(`${this.ENDPOINTS.TRABAJADOR}/${idTrabajador}/centros-costo`);
      return response.data;
    } catch (error) {
      console.error('Error fetching centros costo:', error);
      throw error;
    }
  }

  public static async saveCentroCosto(idTrabajador: number, cc: TrabajadorCentroCosto): Promise<TrabajadorCentroCosto> {
    try {
      const response = await http.post(`${this.ENDPOINTS.TRABAJADOR}/${idTrabajador}/centros-costo`, cc);
      return response.data;
    } catch (error) {
      console.error('Error saving centro costo:', error);
      throw error;
    }
  }

  // ============ Catálogos ============

  public static async getRegimenesLaborales(): Promise<RegimenLaboral[]> {
    const response = await http.get(`${this.ENDPOINTS.CATALOG}/regimenes-laborales`);
    return response.data;
  }

  public static async getModalidadesContrato(): Promise<ModalidadContrato[]> {
    const response = await http.get(`${this.ENDPOINTS.CATALOG}/modalidades-contrato`);
    return response.data;
  }

  public static async getSituaciones(): Promise<SituacionTrabajador[]> {
    const response = await http.get(`${this.ENDPOINTS.CATALOG}/situaciones`);
    return response.data;
  }

  public static async getCategorias(): Promise<CategoriaTrabajador[]> {
    const response = await http.get(`${this.ENDPOINTS.CATALOG}/categorias-trabajador`);
    return response.data;
  }

  public static async getMotivosCese(): Promise<MotivoCese[]> {
    const response = await http.get(`${this.ENDPOINTS.CATALOG}/motivos-cese`);
    return response.data;
  }

  public static async getAreas(): Promise<Area[]> {
    const response = await http.get(`${this.ENDPOINTS.CATALOG}/areas`);
    return response.data;
  }

  public static async getRegimenesPensionarios(): Promise<RegimenPensionario[]> {
    const response = await http.get(this.ENDPOINTS.REGIMEN_PENSIONARIO);
    return response.data;
  }

  public static async getConceptosPlanilla(): Promise<ConceptoPlanilla[]> {
    const response = await http.get(this.ENDPOINTS.CONCEPTO);
    return response.data;
  }
}
